import { FileQuestion, LayoutDashboard, ListChecks, Settings, Users } from 'lucide-react';

import { groupsOf, type AccessGroup, type Session } from '@/api/auth';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: typeof Users;
  /** Which groups may see the item at all. §5: an item is hidden, never disabled. */
  groups: AccessGroup[];
}

export const NAV_ITEMS: NavItem[] = [
  {
    to: '/',
    labelKey: 'dashboard',
    icon: LayoutDashboard,
    groups: ['Admin', 'Recruiter', 'Interviewer'],
  },
  {
    to: '/candidates',
    labelKey: 'candidates',
    icon: Users,
    groups: ['Admin', 'Recruiter', 'Interviewer'],
  },
  /**
   * §6.8 named this route `/roles` and this table shipped `/vacancies`. Settled towards the app on
   * 2026-07-25, exactly as §6.1's `/login` → `/sign-in` was: **`DESIGN.md` §6.8 now reads
   * `/vacancies`**, and the note there records why — every
   * user-visible string for this screen is "Vacancies", and `Role` is pinned in
   * `backend/CONTEXT.md` as an internal term ("always means a vacancy and never access rights") in
   * a product that also has access `Group`s.
   *
   * ⚠️ **The API is not renamed and stays `/api/v1/roles/`.** This is the address bar, not the
   * endpoint, and nothing about the client's URL reaches the server's.
   */
  { to: '/vacancies', labelKey: 'vacancies', icon: ListChecks, groups: ['Admin', 'Recruiter'] },
  /**
   * ⚠️ **All three groups since Sprint 19, and it is the item whose row §5's table has reversed** (ADR
   * 0018, 2026-08-11). It read `groups: ['Admin']`, on the bank's own permission row — `get BANK`
   * answered a **Recruiter and an Interviewer 403, read included**, a rule now **reversed**.
   *
   * ⚠️ **The premise under that conclusion is a paragraph of its own since 2026-09-24, and the split is
   * the finding rather than a tidy-up.** It read on one line with the sentence above, so
   * `retractedClaims.test.ts` read the **`reversed`** of §5's permission row as the retraction of the
   * library clause too and let two of this sprint's own registered forms stand in the present tense —
   * `BUG-098`'s laundering, inside the file the sprint re-counted. A marker now sits in the paragraph
   * whose claim it is about, which is the rule that file enforces everywhere else.
   *
   * ~~A question belongs to the tenant now and every group reads the library: *"a library nobody can
   * read is not a library"*.~~ ⚠️ **That premise is retracted and the conclusion re-derived rather than
   * dropped** (ADR 0019, 2026-09-24): a set owns its questions, and nothing in this product enumerates
   * a tenant's questions any more, so the slogan has no noun left to be about. `/questions` answers the
   * tenant's **sets**, and `GET /api/v1/question-sets/` answers all three groups `200` with the **rows
   * narrowed per reader** — an `Admin` or a `Recruiter` meets the workspace's sets, anybody else meets
   * the sets they wrote plus the sets offered by a vacancy their own assignments touch. Narrowed, and
   * refused to **nobody**: there is no session this route answers `403`, so a group gate here would
   * hide a screen every reader has something of their own on. So the item is everybody's, `/questions`
   * carries no `RequireGroup` (`routes.tsx`), and what a group may **write** is a fact about each row
   * rather than about this table.
   *
   * ⚠️ **`/questions` is the screen and no longer the way in to one.** It answered a chooser — the
   * tenant's vacancies, each a link into its own bank — while §6.9's screen lived at
   * `/vacancies/:vacancyId/questions`. That address has no screen and no API behind it, and the model
   * the chooser taught — *"each vacancy owns its bank"* — is **retracted** with it.
   *
   * The paragraph this replaces recorded why the item exists at all: §5's table lists five items and
   * this one was missing, which quietly made TASK-057's criterion (*"an Interviewer does not see … the
   * question bank"*) true for **every** group, including the Admin it was meant for — a criterion
   * nothing could fail. That criterion is now retired rather than satisfied: an Interviewer **does**
   * see this item, deliberately.
   */
  {
    to: '/questions',
    labelKey: 'questionBank',
    icon: FileQuestion,
    groups: ['Admin', 'Recruiter', 'Interviewer'],
  },
  { to: '/settings', labelKey: 'settings', icon: Settings, groups: ['Admin'] },
];

/**
 * The navigation a session may see. **An item a group may not use is not rendered** (§5) — not
 * disabled, not greyed out. A disabled item still tells the viewer the screen exists, and on a tool
 * where an Interviewer must not learn what a Recruiter can do, that is information we do not owe
 * them.
 *
 * It lives beside `AppShell` rather than inside it because a module that exports both a component
 * and a plain function breaks Fast Refresh (`react-refresh/only-export-components`), and the CI
 * lint job runs with `--max-warnings 0`.
 */
export function visibleNavItems(session: Session): NavItem[] {
  const groups = groupsOf(session);
  return NAV_ITEMS.filter((item) => item.groups.some((group) => groups.includes(group)));
}
